import React, { useState } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  Warning,
  Error,
  Info,
  InfoOutlined
} from '@mui/icons-material';
import { withStyles } from 'tss-react/mui';
import { green, orange, red, blue, grey } from '@mui/material/colors';
import {
  data as docStatus,
  DOCVAL,
  DOCENC,
  DOCREF
} from '@lba-dev/package.local-globals/docStatus';

const styles = () => ({
  root: {
    marginTop: 8,
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: grey[50],
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: grey[100]
    }
  },
  headerTitle: {
    fontSize: 13,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  content: {
    padding: 12,
    backgroundColor: 'white'
  },
  checkItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 12,
    padding: 8,
    borderRadius: 4,
    backgroundColor: grey[50]
  },
  checkIcon: {
    marginTop: 2
  },
  checkContent: {
    flex: 1
  },
  checkTitle: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 4
  },
  checkMessage: {
    fontSize: 11,
    color: grey[700],
    marginBottom: 2
  },
  checkDetails: {
    fontSize: 10,
    color: grey[600],
    fontStyle: 'italic'
  },
  section: {
    marginBottom: 16
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 8,
    color: grey[800]
  },
  dataSourceItem: {
    fontSize: 11,
    marginBottom: 4,
    padding: '4px 8px',
    backgroundColor: blue[50],
    borderRadius: 4,
    borderLeft: `3px solid ${blue[500]}`
  },
  dataSourceLabel: {
    fontWeight: 600,
    color: blue[900]
  },
  dataSourceValue: {
    color: grey[700],
    marginLeft: 4
  },
  warningsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0
  },
  warningItem: {
    fontSize: 11,
    padding: '4px 8px',
    marginBottom: 4,
    backgroundColor: orange[50],
    borderRadius: 4,
    borderLeft: `3px solid ${orange[500]}`,
    color: orange[900]
  },
  errorItem: {
    fontSize: 11,
    padding: '4px 8px',
    marginBottom: 4,
    backgroundColor: red[50],
    borderRadius: 4,
    borderLeft: `3px solid ${red[500]}`,
    color: red[900]
  },
  recommendation: {
    padding: 12,
    borderRadius: 4,
    marginTop: 12,
    fontWeight: 600,
    fontSize: 12
  }
});

const getStatusIcon = (status) => {
  switch (status) {
    case 'VALID':
      return <CheckCircle style={{ color: green[500], fontSize: 18 }} />;
    case 'WARNING':
      return <Warning style={{ color: orange[500], fontSize: 18 }} />;
    case 'INVALID':
      return <Error style={{ color: red[500], fontSize: 18 }} />;
    case 'NOT_APPLICABLE':
      return <Info style={{ color: grey[400], fontSize: 18 }} />;
    default:
      return null;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'VALID':
      return green[50];
    case 'WARNING':
      return orange[50];
    case 'INVALID':
      return red[50];
    case 'NOT_APPLICABLE':
      return grey[50];
    default:
      return grey[50];
  }
};

const InsuranceValidationDetails = ({ classes, metadata }) => {
  const [expanded, setExpanded] = useState(false);

  if (!metadata || !Array.isArray(metadata)) {
    return null;
  }

  const checks = metadata.filter(item => item.type === 'check');
  const recommendation = metadata.find(
    item => item.type === 'recommendation'
  );
  const reviewReason = recommendation?.message;

  return (
    <Box className={classes.root}>
      <Box className={classes.header} onClick={() => setExpanded(!expanded)}>
        <Typography className={classes.headerTitle}>
          <Info style={{ fontSize: 16 }} />
          Détails de validation IA
        </Typography>
        <IconButton
          size="small"
          style={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s'
          }}
        >
          <ExpandMore />
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box className={classes.content}>
          {/* Checklist de validation */}
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              Checklist de validation
            </Typography>
            {checks && checks.map((check) => (
              <Box
                key={check.champ}
                className={classes.checkItem}
                style={{ backgroundColor: getStatusColor(check.status) }}
              >
                <Box className={classes.checkIcon}>
                  {getStatusIcon(check.status)}
                </Box>
                <Box className={classes.checkContent}>
                  <Box
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <Typography className={classes.checkTitle}>
                      {check.label}
                    </Typography>
                    {check.details && (
                      <Tooltip
                        title={check.details}
                        arrow
                        placement="top"
                      >
                        <InfoOutlined
                          style={{
                            fontSize: '14px',
                            color: grey[500],
                            cursor: 'help'
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <Typography className={classes.checkMessage}>
                    {check.message}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Recommandation finale */}
          {recommendation && (() => {
            const recommendationMap = {
              'APPROVE': DOCVAL,
              'REVIEW_REQUIRED': DOCENC,
              'REJECT': DOCREF
            };
            const statusId = recommendationMap[recommendation.value];
            const status = docStatus.find(s => s._id === statusId);
            const bgColor = status ? status.color[50] : grey[50];
            const borderColor = status ? status.color[500] : grey[500];
            return (
              <Box
                className={classes.recommendation}
                style={{
                  backgroundColor: bgColor,
                  borderLeft: `4px solid ${borderColor}`
                }}
              >
                <Typography
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    marginBottom: 4
                  }}
                >
                  Recommandation : {status ? status.name : 'Inconnu'}
                </Typography>
                {reviewReason && (
                  <Typography style={{ fontSize: 11, color: grey[700] }}>
                    Raison : {reviewReason}
                  </Typography>
                )}
              </Box>
            );
          })()}
        </Box>
      </Collapse>
    </Box>
  );
};

export default withStyles(InsuranceValidationDetails, styles);
